/**
 * PdCaptcha — generic Google reCAPTCHA v3 helper (invisible, score-based).
 *
 *   PdCaptcha.enabled()        -> true when the site emitted <meta name="pd-captcha-key"> (feature ON in the site settings)
 *   PdCaptcha.token(action)    -> Promise<string|null>; null on ANY failure (ad blocker, timeout, feature off)
 *
 * Google's api.js is loaded LAZILY on the first token() call, never on page load (10 s timeout).
 * Declarative hook: an element with data-pd-captcha="<action>" inside a <form> gets a fresh token written into a
 * hidden input `pd_captcha_token` just before the form submits. The server (ICaptchaVerifier) decides — a null token
 * is simply sent as « no token » and refused there when the feature is on (fail closed).
 */
(function () {
    'use strict';

    var LOAD_TIMEOUT_MS = 10000;
    var loadPromise = null;

    function siteKey() {
        var m = document.querySelector('meta[name="pd-captcha-key"]');
        var k = m && m.getAttribute('content');
        return k ? k.trim() : '';
    }

    function enabled() { return !!siteKey(); }

    // Resolves the grecaptcha object once ready, or null (blocked, offline, timeout). Cached on success only.
    function load(key) {
        if (window.grecaptcha && window.grecaptcha.execute) return Promise.resolve(window.grecaptcha);
        if (loadPromise) return loadPromise;
        loadPromise = new Promise(function (resolve) {
            var done = false;
            function finish(v) {
                if (done) return;
                done = true;
                clearTimeout(timer);
                if (!v) loadPromise = null; // let a later click retry
                resolve(v);
            }
            var timer = setTimeout(function () { finish(null); }, LOAD_TIMEOUT_MS);
            try {
                var s = document.createElement('script');
                s.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(key);
                s.async = true;
                s.onerror = function () { finish(null); };
                s.onload = function () {
                    if (!window.grecaptcha || !window.grecaptcha.ready) { finish(null); return; }
                    window.grecaptcha.ready(function () { finish(window.grecaptcha); });
                };
                document.head.appendChild(s);
            } catch (e) { finish(null); }
        });
        return loadPromise;
    }

    function token(action) {
        var key = siteKey();
        if (!key) return Promise.resolve(null);
        return load(key).then(function (g) {
            if (!g) return null;
            // execute() can hang if Google answers badly: same 10 s ceiling.
            return Promise.race([
                g.execute(key, { action: String(action || 'submit').replace(/[^A-Za-z0-9_\/]/g, '_') }),
                new Promise(function (r) { setTimeout(function () { r(null); }, LOAD_TIMEOUT_MS); })
            ]).then(function (t) { return t || null; });
        }).catch(function () { return null; });
    }

    window.PdCaptcha = { enabled: enabled, token: token };

    // ── Declarative hook ──
    // Capture phase, before the page's own handlers: hold the first submit, fetch a token, write it into the hidden
    // input, then re-submit once (marked, so the second pass goes straight through).
    document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || form.tagName !== 'FORM' || form.__pdCaptchaPass || !enabled()) return;
        var marker = form.querySelector('[data-pd-captcha]');
        if (!marker) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        var submitter = e.submitter || null;
        token(marker.getAttribute('data-pd-captcha') || 'submit').then(function (t) {
            var input = form.querySelector('input[name="pd_captcha_token"]');
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'pd_captcha_token';
                form.appendChild(input);
            }
            input.value = t || '';
            form.__pdCaptchaPass = true;
            try {
                if (form.requestSubmit) form.requestSubmit(submitter || undefined); else form.submit();
            } finally {
                form.__pdCaptchaPass = false;
            }
        });
    }, true);
})();
